import { useAuth0 } from '@auth0/auth0-react';
import { Switch, Typography } from '@remarkable/ark-web';
import { useQuery } from '@tanstack/react-query';
import type { ChangeEvent, FormEvent } from 'react';
import { useState } from 'react';
import { ButtonClicked } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import {
  queryEntepriseOrgSettings,
  useEnterpriseOrgSettings,
  useUpdateOrgDisplayName,
  useUpdateOrgSettings,
} from 'src/api/queries/enterpriseAdmin';
import { useAccessTokenClaims } from 'src/api/queries/user';
import { Button, Feedback, Input, SomethingWentWrong } from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { Link2 } from 'src/components/Link2';
import { StartEnterpriseDomainForwardingNotification } from 'src/components/StartEnterpriseDomainForwardingNotification';
import { StartEnterpriseMigrationNotifiaction } from 'src/components/StartEnterpriseMigrationNotification';
import { TestId } from 'src/testids';
import { enterpriseHelpers } from 'src/utils/enterpriseHelpers';
import { URLS } from 'src/utils/urls/urls';
import { userHelpers } from 'src/utils/userHelpers';

export function EnterpriseSettingsPage() {
  const accessTokenClaims = useAccessTokenClaims();
  const orgSettings = useEnterpriseOrgSettings();
  const primaryDomain = enterpriseHelpers.getPrimaryDomain(orgSettings.data);

  const canUpdateOrgSettings =
    accessTokenClaims.data &&
    userHelpers.hasPermissionUpdateOrgSettings(accessTokenClaims.data);

  const canStartMigration =
    canUpdateOrgSettings &&
    !enterpriseHelpers.isMigrationEnabled(primaryDomain) &&
    !enterpriseHelpers.isDomainForwardingEnabled(primaryDomain);

  const canStartDomainForwarding =
    canUpdateOrgSettings &&
    enterpriseHelpers.isMigrationEnabled(primaryDomain) &&
    !enterpriseHelpers.isDomainForwardingEnabled(primaryDomain);

  return (
    <>
      <AppPage.Header
        className="w-full justify-start px-16 text-left lm:px-32"
        title="Settings"
      />
      <AppPage.Content
        className="max-w-l2xl"
        data-cy={TestId.PageEnterpriseDomains}
      >
        <div className="flex flex-col gap-32">
          {canStartMigration && <StartEnterpriseMigrationNotifiaction />}

          {canStartDomainForwarding && (
            <StartEnterpriseDomainForwardingNotification />
          )}
          <EnterpriseSettings canUpdateOrgSettings={canUpdateOrgSettings} />
        </div>
        <Feedback
          feedbackTargetId="enterprise-settings-page"
          title="Did you find what you were looking for?"
        />
      </AppPage.Content>
    </>
  );
}

export function EnterpriseSettings({
  canUpdateOrgSettings,
}: {
  canUpdateOrgSettings: boolean;
}) {
  const orgInfo = useQuery(queryEntepriseOrgSettings());
  const updateOrgSettingsMutation = useUpdateOrgSettings();

  const handleSaveSettings = (event: ChangeEvent<HTMLInputElement>) => {
    const analyticsOptout = !event.target.checked;
    if (canUpdateOrgSettings) {
      updateOrgSettingsMutation.mutate({
        analyticsOptout,
      });
    }
  };

  if (orgInfo.isError) {
    return <SomethingWentWrong error={orgInfo.error} />;
  }

  const hasAnalyticsOptOut = orgInfo.data?.analyticsOptout ?? false;

  return (
    <div className="flex flex-col gap-16">
      <OrganizationDisplayName canEdit={canUpdateOrgSettings} />
      <div className="mb-96 flex flex-col gap-8">
        <Typography as="h2" variant="heading-subtle-xl">
          Analytics and reporting
        </Typography>
        <div className="flex items-center gap-[72px]">
          <div>
            <Typography variant="body-md">
              Keep this setting enabled to help us improve our products and
              services. If you turn it off, we&apos;ll no longer use your data
              for analytics, but we&apos;ll continue to collect what&apos;s
              needed for our services to work.
              <br />
              <br />
              This setting applies to all users in your Connect for Business
              subscription and only covers activity on their devices and our
              cloud services. It does not apply to our websites (like{' '}
              <Link2
                inline
                to={URLS.HOME}
                componentLocation={ComponentLocations.ENTERPRISE.SETTINGS_PAGE}
              >
                my.remarkable.com
              </Link2>
              ), where each user manages their analytics consent individually
              through cookie settings.
            </Typography>
          </div>
          <div className="flex-1">
            <Switch
              name="analyticsOptout"
              aria-label="Use analytics consent"
              onChange={handleSaveSettings}
              className="mr-48 justify-self-end"
              checked={!hasAnalyticsOptOut}
              disabled={!canUpdateOrgSettings}
              onClick={() => {
                if (hasAnalyticsOptOut) {
                  tracker.trackEvent(
                    new ButtonClicked({
                      component_location:
                        ComponentLocations.ENTERPRISE.SETTINGS_PAGE,
                      text: 'Disabled',
                      action: 'enable organization analytics',
                    })
                  );
                } else {
                  tracker.trackEvent(
                    new ButtonClicked({
                      component_location:
                        ComponentLocations.ENTERPRISE.SETTINGS_PAGE,
                      text: 'Enabled',
                      action: 'disable organization analytics',
                    })
                  );
                }
              }}
            >
              {hasAnalyticsOptOut ? 'Disabled' : 'Enabled'}
            </Switch>
          </div>
        </div>
      </div>
    </div>
  );
}

const DISPLAY_NAME_MAX_LENGTH = 255;

function OrganizationDisplayName({ canEdit }: { canEdit: boolean }) {
  const auth = useAuth0();
  const updateDisplayName = useUpdateOrgDisplayName();
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState('');

  // Read from the token claims so this always matches the name in the header.
  const displayName: string =
    (auth.user && userHelpers.getOrganizationDisplayName(auth.user)) || '';

  const trimmedDraftName = draftName.trim();
  const canSave =
    trimmedDraftName.length > 0 && trimmedDraftName !== displayName;

  const startEditing = () => {
    setDraftName(displayName);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    updateDisplayName.reset();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSave || updateDisplayName.isPending) return;

    updateDisplayName.mutate(
      { displayName: trimmedDraftName },
      { onSuccess: () => setIsEditing(false) }
    );
  };

  return (
    <div className="mb-48 flex flex-col gap-8">
      <Typography as="h2" variant="heading-subtle-xl">
        Organization name
      </Typography>
      {isEditing ? (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-16 lm:flex-row lm:items-center"
          data-cy="organization-display-name-form"
        >
          <Input
            name="organizationDisplayName"
            aria-label="Organization name"
            value={draftName}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setDraftName(event.currentTarget.value)
            }
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                cancelEditing();
              }
            }}
            maxLength={DISPLAY_NAME_MAX_LENGTH}
            disabled={updateDisplayName.isPending}
            focusOnIsEditable
            required
          />
          <div className="flex gap-8">
            <Button
              type="button"
              variant="secondary"
              size="medium"
              onClick={cancelEditing}
              disabled={updateDisplayName.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="medium"
              loading={updateDisplayName.isPending}
              disabled={!canSave}
              analytics={{
                location: ComponentLocations.ENTERPRISE.SETTINGS_PAGE,
                action: 'update organization display name',
              }}
            >
              Save
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex items-center justify-between gap-16">
          <Typography
            variant="body-md"
            className="truncate"
            data-cy="organization-display-name"
          >
            {displayName || '-'}
          </Typography>
          {canEdit && (
            <Button
              variant="secondary"
              size="medium"
              onClick={startEditing}
              analytics={{
                location: ComponentLocations.ENTERPRISE.SETTINGS_PAGE,
                action: 'edit organization display name',
              }}
            >
              Edit
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
